import { OntologyValueType } from 'features/dynamicOntology'
import { createPatchData } from 'features/fde'
import { useTicketUpdate } from 'features/libra/ticket/TicketUpdateContext'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ConditionalFormConfig,
  ConditionalFormSection,
  ConditionalFormTicketProps,
  ConditionalFormTicketPropsSchema,
} from './api/conditionalFormApi'
import { collectDefaultsForFields } from './conditionalFormDefaults'
import { getVisibleSectionIds } from './conditionalFormSection/conditionalFormVisibility'

type VirtualObjectState = OntologyValueType | undefined

type UseConditionalFormTicketFieldValuesOptions = {
  ontologyFields: ConditionalFormConfig['ontologyFields']
  sections: ReadonlyArray<ConditionalFormSection>
  form: ConditionalFormConfig['form']
}

export const useConditionalFormTicketFieldValues = ({
  ontologyFields,
  sections,
  form,
}: UseConditionalFormTicketFieldValuesOptions) => {
  const { ticketProps, updateTicketProps, isLoading } = useTicketUpdate()

  const { fieldValues: initialFieldValues } = useMemo(
    () => ConditionalFormTicketPropsSchema.parse(ticketProps),
    [ticketProps]
  )

  const [fieldValues, setFieldValues] =
    useState<VirtualObjectState>(initialFieldValues)

  // Update the virual object if the values change under our feet
  useEffect(() => {
    setFieldValues(initialFieldValues)
  }, [initialFieldValues])

  const normalizedFieldValues = useMemo(
    () =>
      (fieldValues as ConditionalFormTicketProps['fieldValues']) ??
      ({} as ConditionalFormTicketProps['fieldValues']),
    [fieldValues]
  )

  const visibleSections = useMemo(
    () =>
      getVisibleSectionIds(form, normalizedFieldValues)
        .map((sectionId) =>
          sections.find((section) => section.id === sectionId)
        )
        .filter(
          (section): section is ConditionalFormSection => section !== undefined
        ),
    [form, normalizedFieldValues, sections]
  )

  const visibleFields = useMemo(
    () => visibleSections.flatMap((section) => section.fields),
    [visibleSections]
  )

  const defaultsForVisibleFields = useMemo(
    () =>
      collectDefaultsForFields(
        normalizedFieldValues,
        ontologyFields,
        visibleFields
      ),
    [normalizedFieldValues, ontologyFields, visibleFields]
  )

  const fieldValuesWithDefaults = useMemo<
    ConditionalFormTicketProps['fieldValues']
  >(() => {
    const merged = {
      ...normalizedFieldValues,
      ...defaultsForVisibleFields,
    }
    return Object.fromEntries(
      Object.entries(merged).filter(([, value]) => value !== undefined)
    ) as ConditionalFormTicketProps['fieldValues']
  }, [normalizedFieldValues, defaultsForVisibleFields])

  const hasChanges = useMemo(
    () =>
      createPatchData('id', initialFieldValues, fieldValuesWithDefaults) !==
      undefined,
    [fieldValuesWithDefaults, initialFieldValues]
  )

  const handleCancel = useCallback(() => {
    setFieldValues(initialFieldValues)
  }, [initialFieldValues])

  const handleSave = useCallback(async () => {
    await updateTicketProps({
      ...ticketProps,
      fieldValues: fieldValuesWithDefaults,
    })
  }, [fieldValuesWithDefaults, ticketProps, updateTicketProps])

  return {
    fieldValues,
    fieldValuesWithDefaults,
    initialFieldValues,
    setFieldValues,
    visibleSections,
    hasChanges,
    handleCancel,
    handleSave,
    isLoading,
  }
}

export type UseConditionalFormTicketFieldValuesReturn = ReturnType<
  typeof useConditionalFormTicketFieldValues
>
